import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import { Loader2, TrendingUp, RefreshCw, Wallet, Receipt } from 'lucide-react';

const inr = (n) => '\u20B9' + (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const todayStr = () => new Date().toISOString().slice(0, 10);

const IncentivesPage = () => {
  const [data, setData] = useState(null);
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayStr());
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const ref = useRef({ start, end });
  ref.current = { start, end };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/analytics/incentives', { params: { start: ref.current.start, end: ref.current.end } });
      setData(res.data);
    } catch (err) { if (!silent) toast({ title: 'Failed to load', description: formatApiError(err), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load, start, end]);
  // Live refresh every 20s
  useEffect(() => { const t = setInterval(() => load(true), 20000); return () => clearInterval(t); }, [load]);

  return (
    <div data-testid="admin-incentives">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Incentives</h1>
          <p className="text-gray-500 text-sm mt-1">(Sales − 18% GST) × 1.5% · membership & training only · live</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="h-9" data-testid="incentive-start" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} min={start} max={todayStr()} onChange={(e) => setEnd(e.target.value)} className="h-9" data-testid="incentive-end" /></div>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => load()} data-testid="incentive-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-600 to-cyan-600 text-white"><CardContent className="p-5"><div className="flex items-center justify-between"><div><p className="text-sm opacity-90">Total Incentive</p><p className="text-3xl font-bold mt-1" data-testid="incentive-total">{inr(data?.total_incentive)}</p></div><Wallet className="h-8 w-8 opacity-80" /></div></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-5"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Total Sales</p><p className="text-2xl font-bold text-gray-900 mt-1">{inr(data?.total_sales)}</p></div><Receipt className="h-7 w-7 text-gray-300" /></div></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-5"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Net after 18% GST</p><p className="text-2xl font-bold text-gray-900 mt-1">{inr(data?.total_net)}</p></div><TrendingUp className="h-7 w-7 text-gray-300" /></div></CardContent></Card>
      </div>

      <Card className="shadow-md border-0 bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
          ) : !data?.breakdown?.length ? (
            <div className="text-center py-16 text-gray-500" data-testid="incentive-empty">No qualifying sales in this period.</div>
          ) : (
            <Table data-testid="incentive-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Sales Representative</TableHead>
                  <TableHead className="text-right">Sales Count</TableHead>
                  <TableHead className="text-right">Total Sales</TableHead>
                  <TableHead className="text-right">Net (−18% GST)</TableHead>
                  <TableHead className="text-right">Incentive (1.5%)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.breakdown.map((r) => (
                  <TableRow key={r.sales_rep_id || 'unassigned'} data-testid={`incentive-row-${r.sales_rep_id || 'unassigned'}`}>
                    <TableCell className="font-medium">{r.sales_rep_name}</TableCell>
                    <TableCell className="text-right text-gray-600">{r.sales_count}</TableCell>
                    <TableCell className="text-right">{inr(r.total_sales)}</TableCell>
                    <TableCell className="text-right text-gray-600">{inr(r.net_after_gst)}</TableCell>
                    <TableCell className="text-right font-semibold text-cyan-700">{inr(r.incentive)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IncentivesPage;
